import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import { useForm, Controller } from "react-hook-form";
import { MDBTable, MDBTableHead, MDBTableBody } from "mdb-react-ui-kit";
import { FaArrowLeft, FaAngleRight, FaAngleLeft } from "react-icons/fa";
import { toast } from "react-toastify";
import * as XLSX from "xlsx-js-style";

import { useReportConditions } from "../../hooks/queries";
import { useSearchSchedules } from "../../hooks/mutation";
import { supabase } from "../../supabaseClient";
import {
  loadAdjustmentsByManager,
  adjustedScheduleDayjs,
  adjustedScheduleInEastern,
} from "../../utils/crewTimeAdjustment";
import dayjs from "dayjs";

import HeaderContainer from "../CrewDashboard/HeaderContainer";
import DateInput from "../common/DateInput";
import CustomSelect from "../common/select";

import "mdb-react-ui-kit/dist/css/mdb.min.css";

// Trips are bucketed by their actual CALENDAR date (local time), matching the
// legacy system and airline billing: a trip at 00:05 on June 1 is a June trip,
// even though the airline lists it under the May 31 "operating night" in the
// schedule file. Boundaries are local-day instants (converted to UTC) so the
// window lines up with how ScheduleDate is displayed and the previous day does
// not leak in via the TIMESTAMPTZ/UTC mismatch.
const reportDayStart = (date) => dayjs(date).startOf("day").toISOString();
const reportDayEndExclusive = (date) =>
  dayjs(date).startOf("day").add(1, "day").toISOString();

const Reports = () => {
  const navigate = useNavigate(); // Use useNavigate instead of useHistory

  const { data: conditions, isSuccess: successConditions } =
    useReportConditions();
  const [companyList, clientList, driverList, vehicleList, locationList, managerFilterList] =
    useMemo(() => {
      if (successConditions) {
        const {
          airlineList,
          vehicleList,
          companyList,
          locationList,
          driverList,
          managerList,
        } = conditions;

        return [
          companyList.map((_item) => ({
            label: _item.Description,
            value: _item.CompanyID,
          })),
          airlineList.map((_item) => ({
            label: _item.Description,
            value: _item.AirlineID,
          })),
          driverList.map((_item) => ({
            label: `${_item.FirstName} ${_item.LastName}`,
            value: _item.DriverID,
          })),
          vehicleList.map((_item) => ({
            label: _item.Model,
            value: _item.VehicleID,
          })),
          locationList.map((_item) => ({
            label: _item.Description,
            value: _item.LocationID,
          })),
          (managerList || []).map((_item) => {
            const name =
              `${_item.FirstName} ${_item.LastName}`.trim() || _item.Email;
            const codes = (_item.AirportCodes || []).join(", ");
            return {
              label: codes ? `${name} — ${codes}` : name,
              value: _item.UserID,
            };
          }),
        ];
      } else return [[], [], [], [], [], []];
    }, [conditions, successConditions]);

  const { control, handleSubmit, reset, getValues } = useForm({
    defaultValues: {
      startDate: "",
      endDate: "",
      companyID: undefined,
      airlineID: undefined,
      driverID: undefined,
      vehicleID: undefined,
      locationID: undefined,
      managerID: undefined,
    },
  });
  const [scheduleList, setScheduleList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const onSuccess = useCallback((_data) => {
    setScheduleList(_data.list);
    setTotalCount(_data.count);
  }, []);
  const onError = useCallback(() => {
    reset();
    toast.error("Search failed.");
  }, [reset]);
  const { mutate: searchSchedules, isPending: isSearching } =
    useSearchSchedules(onSuccess, onError);

  const handleSearch = useCallback(
    (_data) => {
      searchSchedules({
        ..._data,
        page: 1,
      });
    },
    [searchSchedules]
  );
  const onClear = useCallback(() => {
    reset();
  }, [reset]);

  const fetchAllData = useCallback(async (searchParams) => {
    const PAGE_SIZE = 1000;
    let allData = [];
    let page = 0;
    let hasMore = true;

    // Manager filter — Schedule.ManagerID is the direct link.
    const applyManagerFilter = (q) =>
      searchParams.managerID ? q.eq("ManagerID", searchParams.managerID) : q;

    // First, get the total count
    let countQuery = supabase
      .from("ScheduleMixed")
      .select("*", { count: "exact", head: true });

    // Apply the same filters for count query, using calendar-day bounds
    // (see helper comment above).
    if (!!searchParams.startDate)
      countQuery = countQuery.gte(
        "ScheduleDate",
        reportDayStart(searchParams.startDate)
      );
    if (!!searchParams.endDate)
      countQuery = countQuery.lt(
        "ScheduleDate",
        reportDayEndExclusive(searchParams.endDate)
      );
    if (!!searchParams.airlineID)
      countQuery = countQuery.eq("AirlineID", searchParams.airlineID);
    if (!!searchParams.driverID)
      countQuery = countQuery.eq("DriverID", searchParams.driverID);
    if (!!searchParams.vehicleID)
      countQuery = countQuery.eq("VehicleID", searchParams.vehicleID);
    if (!!searchParams.companyID)
      countQuery = countQuery.eq("CompanyID", searchParams.companyID);
    if (!!searchParams.flightNo)
      countQuery = countQuery.ilike("FlightNo", `%${searchParams.flightNo}%`);
    if (!!searchParams.locationID) {
      countQuery = countQuery.or(
        `pickuplocationid.eq.${searchParams.locationID},dropofflocationid.eq.${searchParams.locationID}`
      );
    }
    if (!!searchParams.passengers) {
      countQuery = countQuery.eq("Passengers", searchParams.passengers);
    }
    if (!!searchParams.airlineCode && searchParams.airlineCode != "") {
      countQuery = countQuery.ilike(
        "AirlineLetterCode",
        `%${searchParams.airlineCode}%`
      );
    }
    countQuery = applyManagerFilter(countQuery);

    const { count: totalCount, error: countError } = await countQuery;
    if (countError) throw new Error(countError.toString());

    console.log("Total records to fetch:", totalCount);

    // Now fetch data in chunks
    while (hasMore) {
      let query = supabase.from("ScheduleMixed").select(
        `
          ScheduleID,
          ScheduleDate,
          PickupDateTime,
          IsOneOff,
          FlightNo,
          RateOverride,
          AirlineID,
          AirlineLetterCode,
          ManagerID,
          ScheduleStatus: ScheduleStatusID (Code),
          Company: CompanyID (Description),
          TransportationType: TransportationTypeID (Code),
          DriverID,
          Vehicle: VehicleID (Code, Model),
          Pilots,
          FlightAttendants,
          Passengers,
          PickupLocation: pickuplocationid (Description, LocationID, Code, LocationTypeID),
          DropoffLocation: dropofflocationid (Description, LocationID, Code, LocationTypeID)
        `
      );

      // Apply filters (calendar-day bounds — see count query note above)
      if (!!searchParams.startDate)
        query = query.gte(
          "ScheduleDate",
          reportDayStart(searchParams.startDate)
        );
      if (!!searchParams.endDate)
        query = query.lt(
          "ScheduleDate",
          reportDayEndExclusive(searchParams.endDate)
        );
      if (!!searchParams.airlineID)
        query = query.eq("AirlineID", searchParams.airlineID);
      if (!!searchParams.driverID)
        query = query.eq("DriverID", searchParams.driverID);
      if (!!searchParams.vehicleID)
        query = query.eq("VehicleID", searchParams.vehicleID);
      if (!!searchParams.companyID)
        query = query.eq("CompanyID", searchParams.companyID);
      if (!!searchParams.flightNo)
        query = query.ilike("FlightNo", `%${searchParams.flightNo}%`);
      if (!!searchParams.locationID) {
        query = query.or(
          `pickuplocationid.eq.${searchParams.locationID},dropofflocationid.eq.${searchParams.locationID}`
        );
      }
      if (!!searchParams.passengers) {
        query = query.eq("Passengers", searchParams.passengers);
      }
      if (!!searchParams.airlineCode && searchParams.airlineCode != "") {
        query = query.ilike(
          "AirlineLetterCode",
          `%${searchParams.airlineCode}%`
        );
      }

      query = applyManagerFilter(query);

      // Apply ordering and pagination. Order by date so the exported report
      // reads chronologically; ScheduleID breaks ties for a stable page order.
      query = query
        .order("ScheduleDate", { ascending: true })
        .order("ScheduleID", { ascending: true })
        .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

      const { data, error } = await query;
      if (error) throw new Error(error.toString());

      console.log(`Fetched page ${page + 1}: ${data.length} records`);

      if (data.length === 0) {
        hasMore = false;
      } else {
        allData = allData.concat(data);
        page++;

        // Check if we've fetched all data
        if (data.length < PAGE_SIZE) {
          hasMore = false;
        }
      }
    }

    console.log(`Total fetched: ${allData.length} records`);

    // Resolve manager names primarily via Schedule.ManagerID; fall back to
    // location-based lookup (Users.LocationID) for rows with no ManagerID.
    const managerIds = [
      ...new Set(allData.map((item) => item.ManagerID).filter(Boolean)),
    ];
    const managerNamesById = {};
    if (managerIds.length > 0) {
      const { data: mgrRows } = await supabase
        .from("Managers")
        .select("UserID, firstname, lastname, email")
        .in("UserID", managerIds);
      (mgrRows || []).forEach((m) => {
        managerNamesById[m.UserID] =
          `${m.firstname || ""} ${m.lastname || ""}`.trim() ||
          m.email ||
          `Manager ${m.UserID}`;
      });
    }

    const locationDrivers = {};
    const unassignedLocationIds = [
      ...new Set(
        allData
          .filter((item) => !item.ManagerID)
          .flatMap((item) => [
            item.PickupLocation?.LocationID,
            item.DropoffLocation?.LocationID,
          ])
          .filter(Boolean)
      ),
    ];
    if (unassignedLocationIds.length > 0) {
      const { data: usersAtLocs } = await supabase
        .from("Users")
        .select("UserID, LocationID")
        .eq("RoleID", 2)
        .in("LocationID", unassignedLocationIds);
      const fallbackUserIds = [
        ...new Set((usersAtLocs || []).map((u) => u.UserID)),
      ];
      if (fallbackUserIds.length > 0) {
        const { data: mgrRows } = await supabase
          .from("Managers")
          .select("UserID, firstname, lastname, email")
          .in("UserID", fallbackUserIds);
        const nameByUserId = {};
        (mgrRows || []).forEach((m) => {
          nameByUserId[m.UserID] =
            `${m.firstname || ""} ${m.lastname || ""}`.trim() ||
            m.email ||
            `Manager ${m.UserID}`;
        });
        (usersAtLocs || []).forEach((u) => {
          const name = nameByUserId[u.UserID];
          if (!name) return;
          if (!locationDrivers[u.LocationID]) locationDrivers[u.LocationID] = [];
          if (!locationDrivers[u.LocationID].includes(name)) {
            locationDrivers[u.LocationID].push(name);
          }
        });
      }
    }

    // Same display rule the admin UI uses — hotel→airport runs shift earlier
    // by the manager's matching CrewTimeAdjustment window. JetBlue exempt.
    const adjustmentsByManager = await loadAdjustmentsByManager();

    // Airline display: "AC - Air Canada" instead of just "AC" so managers
    // don't have to remember what every 2-letter code means, and codeshare
    // codes (ZX, RV under Air Canada; DHA under Horizon) read as their
    // parent airline once the sheet-owner override kicks in on new imports.
    const airlineIds = [...new Set(allData.map((i) => i.AirlineID).filter(Boolean))];
    const airlineNameById = {};
    if (airlineIds.length > 0) {
      const { data: airlineRows } = await supabase
        .from("Airlines")
        .select("AirlineID, AirlineLetterCode, Description")
        .in("AirlineID", airlineIds);
      (airlineRows || []).forEach((a) => {
        const code = a.AirlineLetterCode || "";
        const desc = (a.Description || "").trim();
        airlineNameById[a.AirlineID] =
          desc && desc !== code ? `${code} - ${desc}` : code;
      });
    }

    return allData.map((_item) => ({
      ScheduleID: _item.ScheduleID,
      ScheduleDate: adjustedScheduleInEastern(_item, adjustmentsByManager),
      Airline: airlineNameById[_item.AirlineID] ?? (_item.AirlineLetterCode ?? ""),
      FlightNo: _item.FlightNo,
      Status: _item.ScheduleStatus?.Code ?? "",
      Company: _item.Company.Description,
      Transport: _item.TransportationType.Code,
      Driver: (() => {
        if (_item.ManagerID && managerNamesById[_item.ManagerID]) {
          return managerNamesById[_item.ManagerID];
        }
        const pickupDrivers = locationDrivers[_item.PickupLocation?.LocationID] || [];
        const dropoffDrivers = locationDrivers[_item.DropoffLocation?.LocationID] || [];
        const allDrivers = [...new Set([...pickupDrivers, ...dropoffDrivers])];
        return allDrivers.length > 0 ? allDrivers.join(", ") : "Unassigned";
      })(),
      Vehicle: _item.Vehicle?.Model ?? "",
      Pilots: 0,
      FA: 0,
      Rate: _item.RateOverride,
      Pass: _item.Passengers ?? 0,
      PU: _item.PickupLocation?.Description ?? "",
      DO: _item.DropoffLocation?.Description ?? "",
    }));
  }, []);

  const onDownload = useCallback(async () => {
    try {
      toast.info(
        "Preparing download... This may take a moment for large datasets."
      );

      const searchParams = getValues();
      const allData = await fetchAllData(searchParams);
      console.log("+++++++++++++++++++", allData.length);

      const headers = [
        "Company",
        "Client",
        "Schedule Date (ET)",
        "FlightNo",
        "Driver",
        "Vehicle",
        "Pass",
        "P/U",
        "D/O",
        "Status",
        "Rate",
      ];

      // Sort ascending by Schedule Date, then FlightNo. Excel's own filter
      // dropdown still sorts alphabetically inside each column, but pre-
      // sorting the rows means the exported sheet reads chronologically by
      // default and same-flight rows sit next to each other.
      const sortedRows = [...allData].sort((a, b) => {
        const da = String(a.ScheduleDate || "");
        const db = String(b.ScheduleDate || "");
        if (da !== db) return da < db ? -1 : 1;
        const fa = String(a.FlightNo || "");
        const fb = String(b.FlightNo || "");
        return fa < fb ? -1 : fa > fb ? 1 : 0;
      });

      const data = sortedRows.map((row) => [
        row.Company || "",
        row.Airline || "",
        row.ScheduleDate || "",
        row.FlightNo || "",
        row.Driver || "",
        row.Vehicle || "",
        row.Pass || "",
        row.PU || "",
        row.DO || "",
        // Show the actual DB status ("DELETED", "CANCELLED", "ACTIVE", …)
        // instead of hardcoding "Active" — otherwise soft-deleted trips
        // read as active on downloaded reports.
        row.Status || "ACTIVE",
        row.Rate || "",
      ]);

      // Create workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

      // Auto-size columns based on the longest value in each column
      const columnWidths = headers.map((header, colIdx) => {
        let maxLen = String(header).length;
        for (const row of data) {
          const cell = row[colIdx];
          const len = cell == null ? 0 : String(cell).length;
          if (len > maxLen) maxLen = len;
        }
        // Add padding; clamp to a reasonable max
        return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
      });
      worksheet["!cols"] = columnWidths;

      // Add styling to the worksheet
      const range = XLSX.utils.decode_range(worksheet["!ref"]);

      // Style header row (row 0) - black background, white text
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[cellAddress])
          worksheet[cellAddress] = { v: headers[col] };
        worksheet[cellAddress].s = {
          fill: {
            fgColor: { rgb: "000000" },
            patternType: "solid",
          },
          font: {
            bold: true,
            color: { rgb: "FFFFFF" },
          },
          alignment: {
            horizontal: "center",
            vertical: "center",
          },
        };
      }

      // Style data rows - alternating white and gray background
      for (let row = 1; row <= range.e.r; row++) {
        const bgColor = row % 2 === 0 ? "FFFFFF" : "F5F5F5"; // White and light gray
        for (let col = range.s.c; col <= range.e.c; col++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (!worksheet[cellAddress]) worksheet[cellAddress] = { v: "" };
          worksheet[cellAddress].s = {
            fill: {
              fgColor: { rgb: bgColor },
              patternType: "solid",
            },
            alignment: {
              horizontal: "center",
              vertical: "center",
            },
          };
        }
      }

      // Excel autofilter — puts a dropdown on every column header so the
      // billing team can filter to just -ADHOC rows (or exclude them), sort
      // any column A-Z / 0-9, and narrow by client / P&U / D/O without
      // creating a pivot table. Excel sorts the values inside the dropdown
      // automatically once the filter is on.
      worksheet["!autofilter"] = { ref: worksheet["!ref"] };
      // Freeze the header row so it stays visible while scrolling.
      worksheet["!views"] = [{ state: "frozen", ySplit: 1 }];

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

      // Generate the XLSX file
      const excelBuffer = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create download link
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `report_${dayjs().format("YYYY-MM-DD_HH-mm")}.xlsx`;
      link.click();

      toast.success(`Download complete! ${allData.length} records exported.`);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Download failed. Please try again.");
    }
  }, [fetchAllData, getValues]);

  const [page, setPage] = useState(1);
  const onNextPage = useCallback(() => {
    setPage((old) => {
      const newPage = old + 1;
      const _data = getValues();
      searchSchedules({
        ..._data,
        page: newPage,
      });
      return newPage;
    });
  }, [searchSchedules, getValues]);
  const onPrevPage = useCallback(() => {
    setPage((old) => {
      const newPage = old - 1;
      const _data = getValues();
      searchSchedules({
        ..._data,
        page: newPage,
      });
      return newPage;
    });
  }, [searchSchedules, getValues]);

  const handleBack = useCallback(() => {
    sessionStorage.getItem("UserRole") == "1"
      ? navigate("/super-admin")
      : navigate("/manager");
  }, [navigate]);
  return (
    <div className="">
      <div className="p-6 w-full pr-6 pl-6">
        {/* Header */}
        <HeaderContainer />

        {/* Live Driver Update */}
        <div className="driver-content m-4">
          <div className="driver-container mr-4">
            <div
              onClick={handleBack}
              className="flex justify-between items-center w-20 mb-4 cursor-pointer"
            >
              <FaArrowLeft />
              <span>Back</span>
            </div>
            <div>
              <p className="text-location">Reports</p>

              <p>
                Access detailed reports on drivers, vehicles, schedules, and
                financial performance—all in one place.
              </p>
            </div>
          </div>
          <div className="w-60 departure-custom-card rounded-lg">
            <div className="flex justify-between">
              <div className="pr-2 w-50">
                <Controller
                  control={control}
                  name="startDate"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex justify-between mb-2">
                      <div>
                        <p>Date Start</p>
                      </div>
                      <div>
                        <DateInput
                          value={value}
                          onChange={onChange}
                          className="custom-input rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="endDate"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex justify-between mb-2">
                      <div>
                        <p>Date End</p>
                      </div>
                      <div>
                        <DateInput
                          value={value}
                          onChange={onChange}
                          className="custom-input rounded-lg"
                        />
                      </div>
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="companyID"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex justify-between mb-2">
                      <div>Company</div>
                      <CustomSelect
                        id="company-select"
                        size="small"
                        options={companyList}
                        value={value}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="airlineID"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex justify-between mb-2">
                      <div>Client</div>
                      <CustomSelect
                        id="client-select"
                        size="small"
                        options={clientList}
                        value={value}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
              </div>
              <div className="w-50">
                <Controller
                  control={control}
                  name="driverID"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex justify-between mb-2">
                      <div>Driver</div>
                      <CustomSelect
                        id="driver-select"
                        size="small"
                        options={driverList}
                        value={value}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="vehicleID"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex justify-between mb-2">
                      <div>Vehicle</div>
                      <CustomSelect
                        id="vehicle-select"
                        size="small"
                        options={vehicleList}
                        value={value}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="locationID"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex justify-between mb-2">
                      <div>Location</div>
                      <CustomSelect
                        id="location-select"
                        size="small"
                        options={locationList}
                        value={value}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
                <Controller
                  control={control}
                  name="managerID"
                  render={({ field: { value, onChange } }) => (
                    <div className="flex justify-between mb-2">
                      <div>Manager</div>
                      <CustomSelect
                        id="manager-filter-select"
                        size="small"
                        options={managerFilterList}
                        value={value}
                        onChange={onChange}
                      />
                    </div>
                  )}
                />
              </div>
            </div>

            <div className="flex justify-between mb-2">
              <button
                disabled={isSearching}
                onClick={handleSubmit(handleSearch)}
                className="justify-between p-2 rounded-lg flex items-center custom-button"
              >
                <span>Search</span>
              </button>
              <div
                onClick={onClear}
                className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Clear</span>
              </div>
              <div
                onClick={onDownload}
                className="justify-between p-2 rounded-lg flex items-center custom-white-button"
              >
                <span>Download Excel</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 mr-0 ml-0">
          <div className="table-responsive">
            <MDBTable striped bordered hover>
              <MDBTableHead>
                <tr>
                  <th className="p-10">Company</th>
                  <th className="p-10">Client</th>
                  <th className="p-10">Schedule Date (ET)</th>
                  <th className="p-10">FlightNo</th>
                  <th className="p-10">Driver</th>
                  <th className="p-10">Vehicle</th>
                  <th className="p-10">Pilots</th>
                  <th className="p-10">F/A</th>
                  <th className="p-10">Pass</th>
                  <th className="p-10">P/U</th>
                  <th className="p-10">D/O</th>
                  <th className="p-10">Delay</th>
                </tr>
              </MDBTableHead>
              <MDBTableBody>
                {scheduleList.map((row) => (
                  <tr key={row.ScheduleID} className="p-10">
                    <td className="p-10">{row.Company}</td>
                    <td className="p-10">{row.Airline}</td>
                    <td className="p-10">{row.ScheduleDate}</td>
                    <td className="p-10">
                      <a
                        href={`https://www.flightaware.com/live/flight/${
                          row.FlightNo.split("-")[0]
                        }`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {row.FlightNo}
                      </a>
                    </td>
                    <td className="p-10">{row.Driver}</td>
                    {/* <td className="p-10">{row.driver_value}</td> */}
                    <td className="p-10">{row.Vehicle}</td>
                    <td className="p-10">{row.Pilots}</td>
                    <td className="p-10">{row.FA}</td>
                    <td className="p-10">{row.Pass}</td>
                    <td className="p-10">{row.PU}</td>
                    <td className="p-10">{row.DO}</td>
                    {/* <td className="p-10">edit</td> */}
                    <td className="p-10">On-Time</td>
                    {/* <td>{row.d}</td> */}
                  </tr>
                ))}
              </MDBTableBody>
            </MDBTable>
          </div>
          <div className="pagination-container">
            <div className="pagination-content">
              <div>
                <p className="pagination-text">
                  Showing <span>{(page - 1) * 10 + 1}</span> to{" "}
                  <span>{Math.min(totalCount, page * 10)}</span> of{" "}
                  <span>{totalCount}</span> results
                </p>
              </div>
              <div>
                <nav className="pagination-nav">
                  <button
                    onClick={onPrevPage}
                    disabled={page == 1 || isSearching}
                    className={`pagination-item ${page == 1 ? "disabled" : ""}`}
                  >
                    <FaAngleLeft className="pagination-icon" />
                  </button>
                  <button
                    onClick={onNextPage}
                    disabled={page * 10 > totalCount || isSearching}
                    className={`pagination-item ${
                      page * 10 > scheduleList.length ? "disabled" : ""
                    }`}
                  >
                    <FaAngleRight className="pagination-icon" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
